import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalCafe as CoffeeIcon,
  LocalBar as BlendIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import drinksApi from "../../api/drinks";
import CustomerRating from "../../components/common/CustomerRating";
import Layout from "../../components/layout/Layout";
import DrinkDetailModal from "./DrinkDetailModal";
import useAuth from "../../hooks/useAuth";
import { canManageDrinks } from "../../utils/permissions";

interface Drink {
  _id: string;
  name: string;
  type: "coffee" | "blend";
  baseTier: "coffee" | "blend";
  description?: string;
  rating?: number;
  noOfRatings?: number;
  ratingSource?: string;
  volumeMl: number;
  quantity?: { value: number; unit: "g" | "ml" } | null;
  temperature: "cold" | "hot" | "ambient";
  imageUrl?: string;
  isActive: boolean;
  isVegan: boolean;
  containsDairy: boolean;
  containsNuts: boolean;
  allergens: string[];
  ingredients: string[];
  nutritionalInfo?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    caffeineMg?: number;
    sugar?: number;
  };
}

const Drinks: React.FC = () => {
  const { admin } = useAuth();
  const canManage = canManageDrinks(admin);
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDrinkId, setSelectedDrinkId] = useState<string | null>(null);

  useEffect(() => {
    fetchDrinks();
  }, [selectedType]);

  const fetchDrinks = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (selectedType !== "all") {
        params.type = selectedType;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const data = await drinksApi.getDrinks(params);
      setDrinks(data.drinks || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch drinks");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchDrinks();
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    if (!canManage) return;
    try {
      await drinksApi.toggleDrink(id);
      setDrinks((prev) =>
        prev.map((d) => (d._id === id ? { ...d, isActive: !currentStatus } : d))
      );
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to toggle status");
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canManage) return;
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await drinksApi.deleteDrink(id);
      setDrinks((prev) => prev.filter((d) => d._id !== id));
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete drink");
    }
  };

  const handleEdit = (id: string) => {
    setSelectedDrinkId(id);
    setModalOpen(true);
  };

  const handleAddNew = () => {
    if (!canManage) return;
    setSelectedDrinkId(null);
    setModalOpen(true);
  };

  const coffeeCount = drinks.filter((d) => d.type === "coffee").length;
  const blendCount = drinks.filter((d) => d.type === "blend").length;

  return (
    <Layout>
      <Box sx={{ p: 1 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="700" sx={{ color: "#1a1a1a" }}>
              Drinks Catalog
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {drinks.length} beverages ({coffeeCount} Coffees, {blendCount} Blends)
            </Typography>
          </Box>
          {canManage && <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ bgcolor: "#000", color: "#fff", textTransform: "none", px: 3, "&:hover": { bgcolor: "#222" } }}
          >
            Add New Drink
          </Button>}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr auto" }, gap: 2, alignItems: "center" }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, ingredients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </form>
            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="coffee">Coffees Only</MenuItem>
              <MenuItem value="blend">Blends / Shakes Only</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDrinks}
              size="medium"
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Drinks Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#000" }} />
          </Box>
        ) : drinks.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h6" color="textSecondary">
              No drinks found.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 3 }}>
            {drinks.map((drink) => (
              <Box key={drink._id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: "1px solid #eee",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    opacity: drink.isActive ? 1 : 0.6,
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={drink.imageUrl || "https://images.unsplash.com/photo-1517256064527-09c73fc73e38?w=500&q=80"}
                    alt={drink.name}
                    sx={{ objectFit: "cover", bgcolor: "#f8f9fa" }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography variant="h6" fontWeight="700" sx={{ fontSize: "1.1rem" }}>
                        {drink.name}
                      </Typography>
                      <Chip
                        icon={drink.type === "coffee" ? <CoffeeIcon fontSize="small" /> : <BlendIcon fontSize="small" />}
                        label={drink.type === "coffee" ? "Coffee" : "Blend"}
                        size="small"
                        color={drink.type === "coffee" ? "primary" : "secondary"}
                        variant="outlined"
                        sx={{ textTransform: "capitalize", fontWeight: 600 }}
                      />
                    </Box>

                    <Box mb={1.5}>
                      <CustomerRating rating={drink.rating} count={drink.noOfRatings || 0} label={`${drink.name} rating`} />
                      {drink.ratingSource === "drink_orders" && <Typography variant="caption" color="text.secondary">From drink-only delivery ratings</Typography>}
                    </Box>
                    {drink.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5, minHeight: 36 }}>
                        {drink.description.length > 80 ? `${drink.description.substring(0, 80)}...` : drink.description}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                      {drink.quantity && <Chip label={`Quantity: ${drink.quantity.value} ${drink.quantity.unit}`} size="small" variant="outlined" />}
                      <Chip label={`${drink.volumeMl || 300} ml`} size="small" variant="filled" />
                      <Chip label={drink.temperature} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                      {drink.isVegan && <Chip label="Vegan" size="small" color="success" variant="outlined" />}
                      {drink.containsNuts && <Chip label="Contains Nuts" size="small" color="warning" variant="outlined" />}
                    </Box>

                    {drink.nutritionalInfo?.calories !== undefined && (
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 1 }}>
                        ⚡ {drink.nutritionalInfo.calories} kcal | Protein: {drink.nutritionalInfo.protein || 0}g
                        {drink.type === "coffee" && drink.nutritionalInfo.caffeineMg ? ` | Caffeine: ${drink.nutritionalInfo.caffeineMg}mg` : ""}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {canManage && <Switch
                          size="small"
                          checked={drink.isActive}
                          onChange={() => handleToggleActive(drink._id, drink.isActive)}
                          color="success"
                        />}
                        <Typography variant="caption" color={drink.isActive ? "success.main" : "text.secondary"}>
                          {drink.isActive ? "Active" : "Inactive"}
                        </Typography>
                      </Box>
                      <Box>
                        <Tooltip title={canManage ? "Edit Drink" : "View Drink"}>
                          <IconButton aria-label={canManage ? "Edit Drink" : "View Drink"} size="small" onClick={() => handleEdit(drink._id)}>
                            {canManage ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {canManage && <Tooltip title="Delete Drink">
                          <IconButton aria-label="Delete Drink" size="small" color="error" onClick={() => handleDelete(drink._id, drink.name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Modal for Add / Edit */}
        <DrinkDetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          drinkId={selectedDrinkId}
          onSuccess={fetchDrinks}
          readOnly={!canManage}
        />
      </Box>
    </Layout>
  );
};

export default Drinks;
